import { Site } from 'src/app/classes/models/site';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { TradeCategory } from 'src/app/interfaces/models/trade-category';

import { Address } from './address';
import { BaseModel } from './base-model';
import { Company } from './company';
import { ProposalRequestFile } from './proposal-request-file';
import { ProposalRequestLineItem } from './proposal-request-line-item';
import { ProposalRequestVendor } from './proposal-request-vendor';
import { QuickSourceProgress } from './quick-source-progress';
import { User } from './user';

export class ProposalRequest extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ProposalRequestProperties: (
    | {
        property: keyof ProposalRequest;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ProposalRequest;
        type: 'alias';
      }
    | {
        property: keyof ProposalRequest;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'SiteId', type: 'column', dataType: 'number' },
    { property: 'UserId', type: 'column', dataType: 'number' },
    { property: 'name', type: 'column', dataType: 'string' },
    { property: 'type', type: 'column', dataType: 'string' },
    { property: 'workAddress', type: 'column', dataType: 'string' },
    { property: 'workAddress2', type: 'column', dataType: 'string' },
    { property: 'workCity', type: 'column', dataType: 'string' },
    { property: 'workZip', type: 'column', dataType: 'string' },
    { property: 'SubNationalDivisionId', type: 'column', dataType: 'number' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'contactFirstName', type: 'column', dataType: 'string' },
    { property: 'contactLastName', type: 'column', dataType: 'string' },
    { property: 'contactPhoneExt', type: 'column', dataType: 'string' },
    { property: 'contactPhone', type: 'column', dataType: 'string' },
    { property: 'contactPhone2Ext', type: 'column', dataType: 'string' },
    { property: 'contactPhone2', type: 'column', dataType: 'string' },
    { property: 'contactEmail', type: 'column', dataType: 'string' },
    { property: 'startDate', type: 'column', dataType: 'date' },
    { property: 'dueDate', type: 'column', dataType: 'date' },
    { property: 'reminderDate', type: 'column', dataType: 'date' },
    { property: 'instructions', type: 'column', dataType: 'string' },
    { property: 'TradeCategoryId', type: 'column', dataType: 'number' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },
    { property: 'notify', type: 'column', dataType: 'boolean' },
    { property: 'multiLine', type: 'column', dataType: 'boolean' },
    { property: 'lI1Desc', type: 'column', dataType: 'string' },
    { property: 'lI2Desc', type: 'column', dataType: 'string' },
    { property: 'lI3Desc', type: 'column', dataType: 'string' },
    { property: 'lI4Desc', type: 'column', dataType: 'string' },
    { property: 'lI5Desc', type: 'column', dataType: 'string' },
    { property: 'releasedDate', type: 'column', dataType: 'date' },
    { property: 'closedDate', type: 'column', dataType: 'date' },
    { property: 'awardedDate', type: 'column', dataType: 'date' },
    { property: 'cancelledDate', type: 'column', dataType: 'date' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'fullContactName', type: 'alias' },
    { property: 'requestStatus', type: 'alias' },
    { property: 'numberOfReplies', type: 'alias' },
    { property: 'spotlightVendorAwarded', type: 'alias' },
    { property: 'spotlightVendorCount', type: 'alias' },

    {
      property: 'ProposalRequestVendors',
      model: 'ProposalRequestVendor',
      type: 'association'
    },
    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'Site', model: 'Site', type: 'association' },
    { property: 'User', model: 'User', type: 'association' },
    {
      property: 'SubNationalDivision',
      model: 'SubNationalDivision',
      type: 'association'
    },
    { property: 'TradeCategory', model: 'TradeCategory', type: 'association' },
    { property: 'Address', model: 'Address', type: 'association' },
    {
      property: 'ProposalRequestLineItems',
      model: 'ProposalRequestLineItem',
      type: 'association'
    },
    {
      property: 'ProposalRequestFiles',
      model: 'ProposalRequestFile',
      type: 'association'
    },
    {
      property: 'QuickSourceProgresses',
      model: 'QuickSourceProgress',
      type: 'association'
    }
  ];

  static {
    ProposalRequest.ProposalRequestProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        ProposalRequest.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        ProposalRequest.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        ProposalRequest.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ProposalRequestObject: Partial<ProposalRequest>) {
    super();

    ProposalRequest.ProposalRequestProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ProposalRequestObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  CompanyId?: number;
  SiteId?: number;
  UserId?: number;
  name?: string;
  type?: string;

  workAddress?: string;
  workAddress2?: string | null;
  workCity?: string;
  workZip?: string;
  SubNationalDivisionId?: number;

  status?: 'CREATED' | 'RELEASED' | 'CLOSED' | 'AWARDED' | 'CANCELLED';
  contactFirstName?: string;
  contactLastName?: string;
  contactPhoneExt?: string | null;
  contactPhone?: string;
  contactPhone2Ext?: string | null;
  contactPhone2?: string | null;
  contactEmail?: string;
  startDate?: Date;
  dueDate?: Date;
  reminderDate?: Date;
  instructions?: string;
  TradeCategoryId?: number;
  statusDate?: Date;
  isArchived?: boolean;
  notify?: boolean;
  multiLine?: boolean;
  lI1Desc?: string | null;
  lI2Desc?: string | null;
  lI3Desc?: string | null;
  lI4Desc?: string | null;
  lI5Desc?: string | null;
  releasedDate?: Date;
  closedDate?: Date;
  awardedDate?: Date;
  cancelledDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;

  // Aliased columns
  fullContactName?: string;
  requestStatus?: string;
  numberOfReplies?: number;
  spotlightVendorAwarded?: boolean;
  spotlightVendorCount?: number;

  ProposalRequestVendors?: ProposalRequestVendor[];
  ProposalRequestFiles?: ProposalRequestFile[];
  Company?: Company;
  Site?: Site;
  User?: User;
  SubNationalDivision?: SubNationalDivision;
  TradeCategory?: TradeCategory;
  Address?: Address;
  ProposalRequestLineItems?: ProposalRequestLineItem[];
  QuickSourceProgresses?: QuickSourceProgress[];
}
